import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";

const Footer = () => {
  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about-us" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Blog", href: "/blog" },
    { name: "FAQs", href: "/faq" },
  ];

  const services = [
    { name: "Website Design", href: "/website-design" },
    { name: "Managed Hosting", href: "/managed-hosting" },
    { name: "Website Care Plans", href: "/website-care-plans" },
    { name: "Security Audits", href: "/security-audits" },
    { name: "Business Email Setup", href: "/business-email-setup" },
    { name: "Google Business Profile", href: "/google-business-profile" },
  ];

  const locationLinks = [
    { name: "Birmingham", href: "/birmingham" },
    { name: "Bromsgrove", href: "/bromsgrove" },
    { name: "Dudley", href: "/dudley" },
    { name: "Henley-in-Arden", href: "/henley-in-arden" },
    { name: "Redditch", href: "/redditch" },
    { name: "Solihull", href: "/solihull" },
    { name: "Worcester", href: "/worcester" },
  ];

  return (
    <footer data-testid="footer" className="bg-zinc-950 text-white py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="/logo.png" 
                alt="CtrlAltComplete" 
                className="h-12 w-12"
              />
              <span className="font-extrabold text-xl">CtrlAltComplete</span>
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              Web design and digital services to help businesses build, manage and improve their online presence.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/ctrlaltcompleteservices"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 bg-zinc-800 flex items-center justify-center hover:bg-primary transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/ctrlaltcomplete_"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 bg-zinc-800 flex items-center justify-center hover:bg-primary transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6">Get In Touch</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-400 text-sm">
                  Office 1, Izabella House,<br />
                  24–26 Regent Place,<br />
                  City Centre,<br />
                  Birmingham, B1 3NJ
                </span>
              </li>
              <li>
                <a
                  href="mailto:enquiries@ctrlaltcomplete.co.uk"
                  aria-label="Send email to CtrlAltComplete"
                  className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-5 h-5 text-zinc-500" />
                  enquiries@ctrlaltcomplete.co.uk
                </a>
              </li>
              <li>
                <a
                  href="tel:01217943680"
                  aria-label="Call CtrlAltComplete"
                  className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors text-sm"
                >
                  <Phone className="w-5 h-5 text-zinc-500" />
                  0121 794 3680
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Locations We Service */}
        <div className="mb-16" data-testid="footer-locations">
          <h4 className="font-bold text-lg mb-4">Locations We Service</h4>
          <div className="flex flex-wrap items-center gap-y-2">
            {locationLinks.map((loc, i) => (
              <span key={loc.name} className="inline-flex items-center">
                <Link
                  to={loc.href}
                  className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
                >
                  {loc.name}
                </Link>
                {i < locationLinks.length - 1 && <span className="text-zinc-600 mx-3">|</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            &copy; {new Date().getFullYear()} CtrlAltComplete. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-zinc-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="text-zinc-500 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
            <button
              onClick={() => window.dispatchEvent(new Event("openCookieSettings"))}
              data-testid="footer-cookie-settings"
              className="text-zinc-500 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
